import { useMutation } from '@apollo/client';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';

import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { BATCH_TRIGGER_TRIMOJI_MISSING_RESUME_IDS } from '@gql/trimoji';

const MissingResumeIdPanel: React.FC = () => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [batchTrigger, { loading }] = useMutation(BATCH_TRIGGER_TRIMOJI_MISSING_RESUME_IDS, {
        onCompleted: (data) => {
            const result = data?.batchTriggerTrimojiMissingResumeIds;
            if (result) {
                showSnackbar(
                    `Analyse lancée pour ${result.triggeredCount} talent(s) sur ${result.totalFound} trouvé(s).`,
                    'success'
                );
            }
        },
        onError: () => {
            showSnackbar("Erreur lors du lancement de l'analyse en masse.", 'error');
        },
    });

    const handleConfirm = () => {
        setConfirmOpen(false);
        batchTrigger({ variables: { limit: 50 } });
    };

    return (
        <Box>
            <Alert
                severity="info"
                sx={{ mb: 2, color: theme.palette.text.primary }}
            >
                L'analyse Trimoji prend <strong>2 à 5 minutes</strong> par CV. Les statuts se mettront à jour
                automatiquement après réception du callback.
            </Alert>

            <Button
                variant="outlined"
                disabled={loading}
                onClick={() => setConfirmOpen(true)}
                startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
                {loading ? 'Analyse en cours...' : 'Lancer l\'analyse en masse (50 max)'}
            </Button>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">Confirmer l'analyse en masse</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        Cette action va déclencher l'analyse Trimoji pour jusqu'à 50 talents ayant un CV
                        téléchargé mais sans identifiant d'analyse (<code>resumeId</code>).
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Les résultats seront disponibles <strong>2 à 5 minutes</strong> après le lancement.
                        Vous pouvez rafraîchir la page pour voir les mises à jour.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} variant="text" color="secondary">
                        Annuler
                    </Button>
                    <Button onClick={handleConfirm} variant="contained">
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MissingResumeIdPanel;
